import type {
  MonitorChannelMessage,
  SyncPreferences,
  ScrollSyncMessage,
  WLSyncMessage,
} from './types';
import { DEFAULT_SYNC_PREFERENCES } from './types';
import { MonitorChannel } from './MonitorChannel';
import type { ViewerSyncAdapter } from '../viewer/types';

// ---------------------------------------------------------------------------
// GWT postMessage types (same BRIT_* contract as OHIF, subset only)
// ---------------------------------------------------------------------------

/** Messages the GWT viewer sends TO the parent via BritSyncBridge.java */
type GWTOutboundMessage =
  | { type: 'BRIT_SCROLL_CHANGED'; seriesInstanceUID: string; imageIndex: number }
  | { type: 'BRIT_WL_CHANGED'; seriesInstanceUID: string; windowCenter: number; windowWidth: number };

/** Messages we send TO the GWT iframe to apply remote state */
type GWTInboundMessage =
  | { type: 'BRIT_SET_SCROLL'; seriesInstanceUID: string; imageIndex: number }
  | { type: 'BRIT_SET_WL'; seriesInstanceUID: string; windowCenter: number; windowWidth: number };

// ---------------------------------------------------------------------------
// Throttle utility
// ---------------------------------------------------------------------------

function throttle<T extends unknown[]>(fn: (...args: T) => void, ms: number): (...args: T) => void {
  let lastCall = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: T) => {
    const now = Date.now();
    const remaining = ms - (now - lastCall);
    if (remaining <= 0) {
      if (timer) { clearTimeout(timer); timer = null; }
      lastCall = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        lastCall = Date.now();
        timer = null;
        fn(...args);
      }, remaining);
    }
  };
}

// ---------------------------------------------------------------------------
// GWTSyncAdapter
// ---------------------------------------------------------------------------

/**
 * Sync adapter for the GWT viewer.
 *
 * The GWT viewer now includes BritSyncBridge.java which emits
 * BRIT_SCROLL_CHANGED and BRIT_WL_CHANGED postMessages when the user
 * navigates or adjusts window/level, and handles BRIT_SET_SCROLL and
 * BRIT_SET_WL commands from the parent.
 *
 * This gives us scroll and W/L sync across windows. Tool, crosshair,
 * and annotation sync are not supported because the GWT viewer manages
 * those through its own Java-based tool system rather than cornerstone
 * tools.
 *
 * Architecture:
 *   [ GWT iframe ] ←postMessage→ [ GWTSyncAdapter ] ←BroadcastChannel→ [ Other Windows ]
 */
export class GWTSyncAdapter implements ViewerSyncAdapter {
  private muted = false;
  private postMessageListener: ((event: MessageEvent) => void) | null = null;
  private channelUnsubscribe: (() => void) | null = null;
  private preferences: SyncPreferences = { ...DEFAULT_SYNC_PREFERENCES };

  private broadcastScrollThrottled: (msg: ScrollSyncMessage) => void;
  private broadcastWLThrottled: (msg: WLSyncMessage) => void;

  private channel: MonitorChannel;
  private windowId: string;
  private iframeRef: () => HTMLIFrameElement | null;

  constructor(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ) {
    this.channel = channel;
    this.windowId = windowId;
    this.iframeRef = iframeRef;

    this.broadcastScrollThrottled = throttle((msg: ScrollSyncMessage) => {
      this.channel.send(msg);
    }, 16);

    this.broadcastWLThrottled = throttle((msg: WLSyncMessage) => {
      this.channel.send(msg);
    }, 100);
  }

  setPreferences(prefs: SyncPreferences): void {
    this.preferences = { ...prefs };
  }

  start(): void {
    // Listen for GWT iframe → parent postMessages
    this.postMessageListener = (event: MessageEvent) => {
      if (typeof event.data?.type !== 'string') return;
      this.handleGWTMessage(event.data as GWTOutboundMessage);
    };
    window.addEventListener('message', this.postMessageListener);

    // Listen for BroadcastChannel → apply to local GWT
    this.channelUnsubscribe = this.channel.subscribe((msg) => {
      this.handleChannelMessage(msg);
    });
  }

  stop(): void {
    if (this.postMessageListener) {
      window.removeEventListener('message', this.postMessageListener);
      this.postMessageListener = null;
    }
    if (this.channelUnsubscribe) {
      this.channelUnsubscribe();
      this.channelUnsubscribe = null;
    }
  }

  // ── GWT → BroadcastChannel (outbound) ──────────────────────────

  private handleGWTMessage(msg: GWTOutboundMessage): void {
    if (this.muted) return;

    switch (msg.type) {
      case 'BRIT_SCROLL_CHANGED':
        if (!this.preferences.scroll) return;
        this.broadcastScrollThrottled({
          type: 'SCROLL_SYNC',
          sourceWindowId: this.windowId,
          seriesInstanceUID: msg.seriesInstanceUID,
          imageIndex: msg.imageIndex,
        });
        break;

      case 'BRIT_WL_CHANGED':
        if (!this.preferences.windowLevel) return;
        this.broadcastWLThrottled({
          type: 'WL_SYNC',
          sourceWindowId: this.windowId,
          seriesInstanceUID: msg.seriesInstanceUID,
          windowCenter: msg.windowCenter,
          windowWidth: msg.windowWidth,
        });
        break;
    }
  }

  // ── BroadcastChannel → GWT (inbound) ───────────────────────────

  private handleChannelMessage(msg: MonitorChannelMessage): void {
    if ('sourceWindowId' in msg && msg.sourceWindowId === this.windowId) return;

    switch (msg.type) {
      case 'SCROLL_SYNC':
        if (!this.preferences.scroll) return;
        this.applyToGWT({
          type: 'BRIT_SET_SCROLL',
          seriesInstanceUID: msg.seriesInstanceUID,
          imageIndex: msg.imageIndex,
        });
        break;

      case 'WL_SYNC':
        if (!this.preferences.windowLevel) return;
        this.applyToGWT({
          type: 'BRIT_SET_WL',
          seriesInstanceUID: msg.seriesInstanceUID,
          windowCenter: msg.windowCenter,
          windowWidth: msg.windowWidth,
        });
        break;

      // Tool, crosshair, annotation, and reset sync are not supported
      // by the GWT viewer — silently ignore.
    }
  }

  private applyToGWT(msg: GWTInboundMessage): void {
    const iframe = this.iframeRef();
    if (!iframe?.contentWindow) return;

    this.muted = true;
    try {
      iframe.contentWindow.postMessage(msg, '*');
    } catch {
      // iframe may not be ready
    }
    requestAnimationFrame(() => { this.muted = false; });
  }
}
